import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Stack from "veneer/Stack";

import { strategiesStoresLoadedSelector } from "cortex/ui/src/fleet/redux-store/strategies/selectors";
import { LOAD_INCIDENTS_BY_DEVICE_ID } from "cortex/ui/src/fleet/redux-store/ui-state/device-incidents/actions";
import { deviceIncidentsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/device-incidents/selectors";
import { unloadIncidentsAction } from "cortex/ui/src/fleet/redux-store/ui-state/incidents/actions";
import DeviceTimeline from "cortex/ui/src/fleet/view/event/component/DeviceTimeline";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { hasLatestFirmware } from "cortex/ui/src/redux-store/devices/selectors";
import {
  defaultArray,
  defaultObject,
} from "cortex/ui/src/redux-store/selectors";
import { userContentsStoresLoadedSelector } from "cortex/ui/src/redux-store/user-contents/selectors";
import Notification from "cortex/ui/src/ui/notification";
import { Panel } from "cortex/ui/src/view/layout";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import ChartsBox from "./charts/ChartsBox";
import DeviceIncidents from "./DeviceIncidents";
import { Device, DeviceStatsApiResponse, Stats } from "./model";
import StatusBox from "./StatusBox";

interface DeviceOverviewProps {
  device?: Device;
  deviceId: string;
  isThirdParty: boolean;
  stats?: Stats;
  storesLoaded: boolean;
}

export function DeviceOverview({
  device,
  deviceId,
  isThirdParty,
  stats,
  storesLoaded: parentStoresLoaded,
}: DeviceOverviewProps) {
  const dispatch = useAppDispatch();
  const errorMessage = "";
  const successMessage = "";
  const applications = useAppSelector(applicationsSelector);
  const applicationsLoaded = useAppSelector(applicationsStoresLoadedSelector);
  const incidents = useAppSelector(deviceIncidentsSelector);
  const strategiesLoaded = useAppSelector(strategiesStoresLoadedSelector);
  const userContentsLoaded = useAppSelector(userContentsStoresLoadedSelector);
  const storesLoaded = useMemo(
    () =>
      applicationsLoaded &&
      strategiesLoaded &&
      userContentsLoaded &&
      parentStoresLoaded,
    [
      applicationsLoaded,
      parentStoresLoaded,
      strategiesLoaded,
      userContentsLoaded,
    ],
  );

  useEffect(() => {
    void createPromiseApiAction(dispatch, LOAD_INCIDENTS_BY_DEVICE_ID, [
      deviceId,
    ]);

    return () => {
      dispatch(unloadIncidentsAction);
    };
  }, [deviceId, dispatch]);

  if (!storesLoaded) {
    return null;
  }

  if (!device) {
    return null;
  }

  const currentStats = (stats?.stats ??
    defaultObject) as DeviceStatsApiResponse;
  const incidentsTableData = (incidents?.incidents ??
    defaultArray) as ReadonlyArray<Incident>;

  return (
    <Stack spacing={2}>
      {!hasLatestFirmware(device || {}) && (
        <Alert severity="warning">
          This device is not running the latest certified firmware. This may
          result in undesirable behavior, please update your firmware&nbsp;
          <Link to="/devices/upgrade/firmware">here</Link>.
        </Alert>
      )}
      <Notification error={errorMessage} success={successMessage} />
      <StatusBox device={device} stats={currentStats} />
      <Panel title="Device Incidents">
        <Box mx={-2}>
          <DeviceIncidents
            sizePerPage={5}
            storesLoaded={storesLoaded}
            tableData={incidentsTableData}
          />
        </Box>
      </Panel>
      <ChartsBox
        applications={applications}
        deviceId={deviceId}
        stats={currentStats}
      />
      {!isThirdParty && device && (
        <DeviceTimeline deviceId={device.device_id} />
      )}
    </Stack>
  );
}

export default DeviceOverview;
